import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Save, ExternalLink, Wrench } from "lucide-react";
import { toast } from "sonner";

export default function OficinaSettingsPage() {
    const [form, setForm] = useState({
        nome: "",
        cor_primaria: "#FF3B30",
        logo_url: "",
        mensagem_boas_vindas: "",
        telefone: "",
        email: "",
        morada: "",
        google_maps_url: "",
        horario: "",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    async function load() {
        try {
            const res = await api.get("/admin/settings");
            setForm(res.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao carregar");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);

    async function save() {
        if (!form.nome.trim()) {
            toast.error("O nome da oficina é obrigatório.");
            return;
        }
        if (!/^#([0-9A-Fa-f]{6})$/.test(form.cor_primaria)) {
            toast.error("Cor primária deve estar no formato hexadecimal (ex: #FF3B30).");
            return;
        }
        const iva = Number(form.iva_taxa);
        if (Number.isNaN(iva) || iva < 0 || iva > 100) {
            toast.error("Taxa de IVA deve estar entre 0 e 100.");
            return;
        }
        setSaving(true);
        try {
            await api.put("/admin/settings", { ...form, iva_taxa: iva });
            toast.success("Configurações guardadas.");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao guardar");
        } finally {
            setSaving(false);
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-300 border-t-[#FF3B30]" />
            </div>
        );
    }

    return (
        <>
            <PageHeader
                eyebrow="Administração"
                title="Identidade da Oficina"
                description="Personalize o nome, logótipo, cor e contactos que os seus clientes vêem na página pública de acompanhamento."
                testid="oficina-settings-header"
                actions={
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                        data-testid="settings-save-btn"
                    >
                        <Save className="mr-2 h-4 w-4" />
                        {saving ? "A guardar..." : "Guardar alterações"}
                    </Button>
                }
            />

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                {/* Form */}
                <div className="space-y-8 lg:col-span-2">
                    <Section title="Marca">
                        <Field label="Nome da oficina *">
                            <Input
                                value={form.nome}
                                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                                className="h-10 rounded-sm"
                                data-testid="settings-nome-input"
                            />
                        </Field>
                        <Field label="URL do logótipo (PNG/SVG)">
                            <Input
                                value={form.logo_url}
                                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                                placeholder="https://..."
                                className="h-10 rounded-sm"
                                data-testid="settings-logo-input"
                            />
                            <p className="mt-1.5 text-xs text-zinc-500">
                                Cole aqui o link público da imagem (ex: alojado no seu site).
                            </p>
                        </Field>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Cor de destaque (HEX)">
                                <div className="flex items-center gap-2">
                                    <Input
                                        value={form.cor_primaria}
                                        onChange={(e) =>
                                            setForm({ ...form, cor_primaria: e.target.value })
                                        }
                                        className="h-10 rounded-sm font-mono"
                                        data-testid="settings-cor-input"
                                    />
                                    <input
                                        type="color"
                                        value={
                                            /^#([0-9A-Fa-f]{6})$/.test(form.cor_primaria)
                                                ? form.cor_primaria
                                                : "#FF3B30"
                                        }
                                        onChange={(e) =>
                                            setForm({ ...form, cor_primaria: e.target.value.toUpperCase() })
                                        }
                                        className="h-10 w-12 cursor-pointer rounded-sm border border-zinc-300 bg-white p-1"
                                        data-testid="settings-cor-picker"
                                    />
                                </div>
                            </Field>
                        </div>
                        <Field label="Mensagem de boas-vindas (página pública)">
                            <Textarea
                                value={form.mensagem_boas_vindas}
                                onChange={(e) =>
                                    setForm({ ...form, mensagem_boas_vindas: e.target.value })
                                }
                                rows={2}
                                placeholder="A sua moto está em boas mãos."
                                className="rounded-sm"
                                data-testid="settings-mensagem-input"
                            />
                        </Field>
                    </Section>

                    <Section title="Contactos">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Field label="Telefone">
                                <Input
                                    value={form.telefone}
                                    onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                                    placeholder="+351 ..."
                                    className="h-10 rounded-sm"
                                    data-testid="settings-telefone-input"
                                />
                            </Field>
                            <Field label="Email">
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="h-10 rounded-sm"
                                    data-testid="settings-email-input"
                                />
                            </Field>
                        </div>
                        <Field label="Morada">
                            <Input
                                value={form.morada}
                                onChange={(e) => setForm({ ...form, morada: e.target.value })}
                                placeholder="Rua, n.º, Cidade"
                                className="h-10 rounded-sm"
                                data-testid="settings-morada-input"
                            />
                        </Field>
                        <Field label="Link Google Maps">
                            <Input
                                value={form.google_maps_url}
                                onChange={(e) =>
                                    setForm({ ...form, google_maps_url: e.target.value })
                                }
                                placeholder="https://maps.google.com/?q=..."
                                className="h-10 rounded-sm"
                                data-testid="settings-maps-input"
                            />
                        </Field>
                        <Field label="Horário">
                            <Textarea
                                value={form.horario}
                                onChange={(e) => setForm({ ...form, horario: e.target.value })}
                                rows={2}
                                placeholder="Seg–Sex 09:00–19:00 · Sáb 09:00–13:00"
                                className="rounded-sm"
                                data-testid="settings-horario-input"
                            />
                        </Field>
                    </Section>

                    <Section title="Faturação">
                        <Field label="Taxa de IVA (%)">
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={form.iva_taxa}
                                    onChange={(e) =>
                                        setForm({ ...form, iva_taxa: e.target.value })
                                    }
                                    className="h-10 w-32 rounded-sm font-mono"
                                    data-testid="settings-iva-input"
                                />
                                <span className="text-sm text-zinc-500">
                                    Aplicada nas folhas de obra (PDF).
                                </span>
                            </div>
                        </Field>
                    </Section>
                </div>

                {/* Preview */}
                <aside className="lg:col-span-1">
                    <div className="sticky top-8">
                        <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-zinc-500">
                            Pré-visualização
                        </div>
                        <div
                            className="border border-zinc-200 bg-white p-6"
                            data-testid="settings-preview"
                        >
                            <div className="flex items-center gap-3 border-b border-zinc-100 pb-4">
                                {form.logo_url ? (
                                    <img
                                        src={form.logo_url}
                                        alt={form.nome}
                                        className="h-10 w-10 rounded-sm border border-zinc-200 object-cover"
                                    />
                                ) : (
                                    <div
                                        className="flex h-10 w-10 items-center justify-center rounded-sm text-white"
                                        style={{
                                            backgroundColor: /^#([0-9A-Fa-f]{6})$/.test(form.cor_primaria)
                                                ? form.cor_primaria
                                                : "#FF3B30",
                                        }}
                                    >
                                        <Wrench className="h-4 w-4" />
                                    </div>
                                )}
                                <div>
                                    <div className="font-['Outfit'] text-base font-semibold tracking-tight">
                                        {form.nome || "Oficina Moto"}
                                    </div>
                                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                                        Acompanhar reparação
                                    </div>
                                </div>
                            </div>
                            <h3 className="mt-4 font-['Outfit'] text-xl font-semibold tracking-tight">
                                {form.mensagem_boas_vindas || "A sua moto está em boas mãos."}
                            </h3>
                            <div className="mt-4 inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                                style={{
                                    color: form.cor_primaria,
                                    borderColor: form.cor_primaria,
                                }}>
                                <span style={{ backgroundColor: form.cor_primaria }} className="h-1.5 w-1.5 rounded-full" />
                                Estado · Em Curso
                            </div>
                            <div className="mt-5 space-y-1 text-xs text-zinc-600">
                                {form.telefone ? <div>Tel: {form.telefone}</div> : null}
                                {form.email ? <div>Email: {form.email}</div> : null}
                                {form.morada ? <div>Morada: {form.morada}</div> : null}
                                {form.horario ? <div className="whitespace-pre-line">{form.horario}</div> : null}
                            </div>
                            <a
                                href="/track/preview"
                                target="_blank"
                                rel="noreferrer"
                                className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-zinc-700 hover:text-[#FF3B30]"
                            >
                                Ver página pública <ExternalLink className="h-3 w-3" />
                            </a>
                        </div>
                    </div>
                </aside>
            </div>
        </>
    );
}

function Section({ title, children }) {
    return (
        <section className="border border-zinc-200 bg-white">
            <div className="border-b border-zinc-200 px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                {title}
            </div>
            <div className="space-y-5 p-5">{children}</div>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                {label}
            </Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
